import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { api } from './services/api';
import rtkQueryErrorLogger from './utils/rtkQueryErrorLogger';
import { tokenMiddleware } from './tokenMiddleware';
import { perfMiddleware } from './utils/perf';
import p2pReducer from './services/p2p/actionSlice';
import profileReducer from './services/profile/actionSlice';
import walletReducer from './services/wallet/actionSlice';
import kycReducer from './services/kyc/actionSlice';
import historyReducer from './services/history/actionSlice';
import otcHistoryReducer from './services/history/otcActionSlice';
import p2pHistorySlice from './services/history/p2pActionSlice';
import marketSlice from './services/market/actionSlice';
import otcMarket from './services/otc/market/marketSlice';
import pnlSlice from './services/portfolio/actionSlice';

// Development environment check
const isDevelopment = import.meta.env.MODE === 'development';

export const store = configureStore({
  reducer: {
    // Add the generated reducer as a specific top-level slice
    [api.reducerPath]: api.reducer,
    p2p: p2pReducer,
    profile: profileReducer,
    wallet: walletReducer,
    kyc: kycReducer,
    history: historyReducer,
    otcHistory: otcHistoryReducer,
    p2pHistory: p2pHistorySlice,
    market: marketSlice,
    otcMarket,
    pnl: pnlSlice
  },
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types that might contain non-serializable data
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'persist/REGISTER',
          // RTK Query actions
          'api/executeQuery/pending',
          'api/executeQuery/fulfilled',
          'api/executeQuery/rejected',
          'api/executeMutation/pending',
          'api/executeMutation/fulfilled',
          'api/executeMutation/rejected'
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: ['meta.arg', 'payload.timestamp'],
        // Ignore these paths in the state
        ignoredPaths: ['api.queries', 'api.mutations'],
        // Increase threshold to reduce warnings
        warnAfter: isDevelopment ? 128 : 32
      },
      immutableCheck: {
        // Ignore these paths for immutability checks
        ignoredPaths: ['api.queries', 'api.mutations'],
        warnAfter: isDevelopment ? 128 : 32
      }
    }).concat(
      api.middleware,
      rtkQueryErrorLogger,
      tokenMiddleware,
      // No-op unless the profiler is switched on (`__perf.on()` in dev).
      perfMiddleware
    )
});

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
